import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Package, Truck, Target, RefreshCw, Database, Shield,
  TrendingUp, TrendingDown, Clock, CheckCircle, AlertCircle,
  Activity, ArrowUp, ArrowDown, Minus, Download
} from 'lucide-react';

const ClientDashboard = () => {
  const { currentUser } = useAuth();
  const [metrics, setMetrics] = useState({
    dockToStock: { value: 0, unit: '%', trend: 0, status: 'good' },
    onTimeShipping: { value: 0, unit: '%', trend: 0, status: 'good' },
    onTimeShippingPeak: { value: 0, unit: '%', trend: 0, status: 'good' },
    shippingAccuracy: { value: 0, unit: '%', trend: 0, status: 'good' },
    inventoryAccuracy: { value: 0, unit: '%', trend: 0, status: 'good' },
    returnsProcessing: { value: 0, unit: '%', trend: 0, status: 'good' }
  });
  const [trendData, setTrendData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('this_month');
  const [selectedMetric, setSelectedMetric] = useState('onTimeShipping');

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange, currentUser]);

  // Generate mock data that looks realistic for a 3PL
  const generateMockData = () => {
    const baseMetrics = {
      dockToStock: {
        value: 99.3 + (Math.random() * 0.4), // 99.3% - 99.7%, target: 99%
        target: 99,
        trend: 0.1 + (Math.random() * 0.3),
        comparison: 'higher is better'
      },
      onTimeShipping: {
        value: 99.6 + (Math.random() * 0.2), // 99.6% - 99.8%, target: 99.5%
        target: 99.5,
        trend: 0.1 + (Math.random() * 0.3),
        comparison: 'higher is better'
      },
      onTimeShippingPeak: {
        value: 87 + (Math.random() * 5), // 87% - 92%, target: 85%
        target: 85,
        trend: 0.5 + (Math.random() * 1.5),
        comparison: 'higher is better'
      },
      shippingAccuracy: {
        value: 99.82 + (Math.random() * 0.13), // 99.82% - 99.95%, target: 99.8%
        target: 99.8,
        trend: 0.05 + (Math.random() * 0.15),
        comparison: 'higher is better'
      },
      inventoryAccuracy: {
        value: 99.82 + (Math.random() * 0.13), // 99.82% - 99.95%, target: 99.8%
        target: 99.8,
        trend: 0.05 + (Math.random() * 0.15),
        comparison: 'higher is better'
      },
      returnsProcessing: {
        value: 99.2 + (Math.random() * 0.4), // 99.2% - 99.6%, target: 99%
        target: 99,
        trend: 0.1 + (Math.random() * 0.3),
        comparison: 'higher is better'
      }
    };

    // Determine status based on target
    Object.keys(baseMetrics).forEach(key => {
      const metric = baseMetrics[key];
      if (metric.comparison === 'higher is better') {
        if (metric.value >= metric.target) {
          metric.status = 'good';
        } else if (metric.value >= metric.target - 2) {
          metric.status = 'warning';
        } else {
          metric.status = 'danger';
        }
      } else {
        if (metric.value <= metric.target) {
          metric.status = 'good';
        } else if (metric.value <= metric.target + 1) {
          metric.status = 'warning';
        } else {
          metric.status = 'danger';
        }
      }
    });

    return baseMetrics;
  };

  const generateTrendData = (metricKey) => {
    const days = dateRange === 'this_month' ? 30 : dateRange === 'this_week' ? 7 : 1;
    const data = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);

      let value;
      switch(metricKey) {
        case 'dockToStock':
          value = 99.3 + (Math.random() * 0.4);
          break;
        case 'onTimeShipping':
          value = 99.6 + (Math.random() * 0.2);
          break;
        case 'onTimeShippingPeak':
          value = 87 + (Math.random() * 5);
          break;
        case 'shippingAccuracy':
          value = 99.82 + (Math.random() * 0.13);
          break;
        case 'inventoryAccuracy':
          value = 99.82 + (Math.random() * 0.13);
          break;
        case 'returnsProcessing':
          value = 99.2 + (Math.random() * 0.4);
          break;
        default:
          value = 99 + (Math.random() * 1);
      }

      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: value
      });
    }

    return data;
  };

  const generateRecentActivity = () => {
    const activities = [
      { type: 'inbound', icon: 'truck', status: 'completed', text: 'Received PO #45821 - 1,240 units', time: '2 hours ago', color: 'blue' },
      { type: 'outbound', icon: 'package', status: 'completed', text: 'Shipped Order #89234 - 450 units', time: '3 hours ago', color: 'green' },
      { type: 'return', icon: 'refresh', status: 'processing', text: 'Processing RMA #7821 - 23 units', time: '4 hours ago', color: 'yellow' },
      { type: 'outbound', icon: 'package', status: 'completed', text: 'Shipped Order #89156 - 890 units', time: '5 hours ago', color: 'green' },
      { type: 'inventory', icon: 'database', status: 'completed', text: 'Cycle count completed - Zone A', time: '6 hours ago', color: 'purple' },
      { type: 'inbound', icon: 'truck', status: 'in_progress', text: 'Receiving PO #45822 - 2,100 units', time: '7 hours ago', color: 'blue' },
      { type: 'outbound', icon: 'package', status: 'completed', text: 'Shipped Order #89087 - 320 units', time: '8 hours ago', color: 'green' },
      { type: 'alert', icon: 'alert', status: 'warning', text: 'Low stock alert - SKU #12345', time: '9 hours ago', color: 'orange' }
    ];

    return activities;
  };

  const generateAlerts = () => {
    const alerts = [
      {
        type: 'warning',
        message: '3 SKUs below reorder point',
        action: 'View Inventory',
        severity: 'medium'
      },
      {
        type: 'info',
        message: 'Cycle count scheduled for tomorrow - Zone B',
        action: 'View Schedule',
        severity: 'low'
      }
    ];

    return alerts;
  };

  const fetchDashboardData = async () => {
    setLoading(true);

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Generate mock data
      const mockMetrics = generateMockData();

      setMetrics({
        dockToStock: {
          value: mockMetrics.dockToStock.value,
          unit: '%',
          trend: mockMetrics.dockToStock.trend,
          status: mockMetrics.dockToStock.status,
          target: mockMetrics.dockToStock.target
        },
        onTimeShipping: {
          value: mockMetrics.onTimeShipping.value,
          unit: '%',
          trend: mockMetrics.onTimeShipping.trend,
          status: mockMetrics.onTimeShipping.status,
          target: mockMetrics.onTimeShipping.target
        },
        onTimeShippingPeak: {
          value: mockMetrics.onTimeShippingPeak.value,
          unit: '%',
          trend: mockMetrics.onTimeShippingPeak.trend,
          status: mockMetrics.onTimeShippingPeak.status,
          target: mockMetrics.onTimeShippingPeak.target
        },
        shippingAccuracy: {
          value: mockMetrics.shippingAccuracy.value,
          unit: '%',
          trend: mockMetrics.shippingAccuracy.trend,
          status: mockMetrics.shippingAccuracy.status,
          target: mockMetrics.shippingAccuracy.target
        },
        inventoryAccuracy: {
          value: mockMetrics.inventoryAccuracy.value,
          unit: '%',
          trend: mockMetrics.inventoryAccuracy.trend,
          status: mockMetrics.inventoryAccuracy.status,
          target: mockMetrics.inventoryAccuracy.target
        },
        returnsProcessing: {
          value: mockMetrics.returnsProcessing.value,
          unit: '%',
          trend: mockMetrics.returnsProcessing.trend,
          status: mockMetrics.returnsProcessing.status,
          target: mockMetrics.returnsProcessing.target
        }
      });

      setTrendData(generateTrendData(selectedMetric));
      setRecentActivity(generateRecentActivity());
      setAlerts(generateAlerts());

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      setTrendData(generateTrendData(selectedMetric));
    }
  }, [selectedMetric]);

  const formatValue = (value, unit) => {
    if (unit === '%') {
      return value.toFixed(1);
    } else if (unit === 'hrs') {
      return value.toFixed(1);
    } else if (unit === 'days') {
      return value.toFixed(1);
    }
    return value.toFixed(2);
  };

  const getTrendIcon = (trend) => {
    if (trend > 0.5) return <ArrowUp className="w-4 h-4" />;
    if (trend < -0.5) return <ArrowDown className="w-4 h-4" />;
    return <Minus className="w-4 h-4" />;
  };

  const getTrendColor = (trend, comparison = 'higher is better') => {
    if (comparison === 'higher is better') {
      if (trend > 0.5) return 'text-green-400';
      if (trend < -0.5) return 'text-red-400';
    } else {
      if (trend > 0.5) return 'text-red-400';
      if (trend < -0.5) return 'text-green-400';
    }
    return 'text-slate-400';
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'good': return 'border-green-500/50 bg-green-900/20';
      case 'warning': return 'border-yellow-500/50 bg-yellow-900/20';
      case 'danger': return 'border-red-500/50 bg-red-900/20';
      default: return 'border-slate-700';
    }
  };

  const getActivityIcon = (iconType) => {
    switch(iconType) {
      case 'truck': return <Truck className="w-4 h-4" />;
      case 'package': return <Package className="w-4 h-4" />;
      case 'refresh': return <RefreshCw className="w-4 h-4" />;
      case 'database': return <Database className="w-4 h-4" />;
      case 'alert': return <AlertCircle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getActivityColor = (color) => {
    switch(color) {
      case 'blue': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'green': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'yellow': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'purple': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'orange': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-700 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="h-32 bg-slate-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">
            {currentUser.businessUnit?.name || 'Client'} Performance Dashboard
          </h1>
          <p className="text-slate-400 mt-1">3PL logistics and fulfillment metrics</p>
        </div>
        <div className="flex gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-slate-700 text-slate-200 border border-slate-600 rounded-lg px-3 py-2 text-sm"
          >
            <option value="today">Today</option>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="last_30_days">Last 30 Days</option>
          </select>
          <button
            onClick={() => fetchDashboardData()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors"
          >
            <Download className="w-4 h-4" />
            Export Report
          </button>
        </div>
      </div>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-yellow-300 font-semibold mb-2">Active Alerts</h3>
              <div className="space-y-2">
                {alerts.map((alert, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-yellow-200 text-sm">{alert.message}</span>
                    <button className="text-yellow-400 hover:text-yellow-300 text-sm underline">
                      {alert.action}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Dock to Stock */}
        <div className={`bg-slate-800 p-6 rounded-lg border-2 ${getStatusColor(metrics.dockToStock.status)} transition-all`}>
          <div className="flex items-start justify-between mb-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Clock className="w-6 h-6 text-blue-400" />
            </div>
            <div className={`flex items-center gap-1 text-sm ${getTrendColor(metrics.dockToStock.trend)}`}>
              {getTrendIcon(metrics.dockToStock.trend)}
              <span>{Math.abs(metrics.dockToStock.trend).toFixed(1)}%</span>
            </div>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-1">Dock to Stock</p>
            <p className="text-3xl font-bold text-slate-100">
              {formatValue(metrics.dockToStock.value, metrics.dockToStock.unit)}
              <span className="text-xl text-slate-400 ml-1">{metrics.dockToStock.unit}</span>
            </p>
            <p className="text-xs text-slate-500 mt-1">Target: ≥ {metrics.dockToStock.target}% within 48hrs</p>
          </div>
        </div>

        {/* On-Time Shipping */}
        <div className={`bg-slate-800 p-6 rounded-lg border-2 ${getStatusColor(metrics.onTimeShipping.status)} transition-all`}>
          <div className="flex items-start justify-between mb-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <Package className="w-6 h-6 text-green-400" />
            </div>
            <div className={`flex items-center gap-1 text-sm ${getTrendColor(metrics.onTimeShipping.trend)}`}>
              {getTrendIcon(metrics.onTimeShipping.trend)}
              <span>{Math.abs(metrics.onTimeShipping.trend).toFixed(1)}%</span>
            </div>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-1">On-Time Shipping</p>
            <p className="text-3xl font-bold text-slate-100">
              {formatValue(metrics.onTimeShipping.value, metrics.onTimeShipping.unit)}
              <span className="text-xl text-slate-400 ml-1">{metrics.onTimeShipping.unit}</span>
            </p>
            <p className="text-xs text-slate-500 mt-1">Target: ≥ {metrics.onTimeShipping.target}% same day</p>
          </div>
        </div>

        {/* On-Time Shipping Peak */}
        <div className={`bg-slate-800 p-6 rounded-lg border-2 ${getStatusColor(metrics.onTimeShippingPeak.status)} transition-all`}>
          <div className="flex items-start justify-between mb-3">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <TrendingUp className="w-6 h-6 text-orange-400" />
            </div>
            <div className={`flex items-center gap-1 text-sm ${getTrendColor(metrics.onTimeShippingPeak.trend)}`}>
              {getTrendIcon(metrics.onTimeShippingPeak.trend)}
              <span>{Math.abs(metrics.onTimeShippingPeak.trend).toFixed(1)}%</span>
            </div>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-1">On-Time Shipping (Peak)</p>
            <p className="text-3xl font-bold text-slate-100">
              {formatValue(metrics.onTimeShippingPeak.value, metrics.onTimeShippingPeak.unit)}
              <span className="text-xl text-slate-400 ml-1">{metrics.onTimeShippingPeak.unit}</span>
            </p>
            <p className="text-xs text-slate-500 mt-1">Target: ≥ {metrics.onTimeShippingPeak.target}% same day (peak)</p>
          </div>
        </div>

        {/* Shipping Accuracy */}
        <div className={`bg-slate-800 p-6 rounded-lg border-2 ${getStatusColor(metrics.shippingAccuracy.status)} transition-all`}>
          <div className="flex items-start justify-between mb-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Target className="w-6 h-6 text-purple-400" />
            </div>
            <div className={`flex items-center gap-1 text-sm ${getTrendColor(metrics.shippingAccuracy.trend)}`}>
              {getTrendIcon(metrics.shippingAccuracy.trend)}
              <span>{Math.abs(metrics.shippingAccuracy.trend).toFixed(1)}%</span>
            </div>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-1">Shipping Accuracy</p>
            <p className="text-3xl font-bold text-slate-100">
              {formatValue(metrics.shippingAccuracy.value, metrics.shippingAccuracy.unit)}
              <span className="text-xl text-slate-400 ml-1">{metrics.shippingAccuracy.unit}</span>
            </p>
            <p className="text-xs text-slate-500 mt-1">Target: ≥ {metrics.shippingAccuracy.target}% without error</p>
          </div>
        </div>

        {/* Inventory Accuracy */}
        <div className={`bg-slate-800 p-6 rounded-lg border-2 ${getStatusColor(metrics.inventoryAccuracy.status)} transition-all`}>
          <div className="flex items-start justify-between mb-3">
            <div className="p-2 bg-cyan-500/20 rounded-lg">
              <Database className="w-6 h-6 text-cyan-400" />
            </div>
            <div className={`flex items-center gap-1 text-sm ${getTrendColor(metrics.inventoryAccuracy.trend)}`}>
              {getTrendIcon(metrics.inventoryAccuracy.trend)}
              <span>{Math.abs(metrics.inventoryAccuracy.trend).toFixed(1)}%</span>
            </div>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-1">Inventory Accuracy</p>
            <p className="text-3xl font-bold text-slate-100">
              {formatValue(metrics.inventoryAccuracy.value, metrics.inventoryAccuracy.unit)}
              <span className="text-xl text-slate-400 ml-1">{metrics.inventoryAccuracy.unit}</span>
            </p>
            <p className="text-xs text-slate-500 mt-1">Target: ≥ {metrics.inventoryAccuracy.target}% WMS accuracy</p>
          </div>
        </div>

        {/* Returns Processing */}
        <div className={`bg-slate-800 p-6 rounded-lg border-2 ${getStatusColor(metrics.returnsProcessing.status)} transition-all`}>
          <div className="flex items-start justify-between mb-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <RefreshCw className="w-6 h-6 text-yellow-400" />
            </div>
            <div className={`flex items-center gap-1 text-sm ${getTrendColor(metrics.returnsProcessing.trend)}`}>
              {getTrendIcon(metrics.returnsProcessing.trend)}
              <span>{Math.abs(metrics.returnsProcessing.trend).toFixed(1)}%</span>
            </div>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-1">Returns Processing</p>
            <p className="text-3xl font-bold text-slate-100">
              {formatValue(metrics.returnsProcessing.value, metrics.returnsProcessing.unit)}
              <span className="text-xl text-slate-400 ml-1">{metrics.returnsProcessing.unit}</span>
            </p>
            <p className="text-xs text-slate-500 mt-1">Target: ≥ {metrics.returnsProcessing.target}% within 2 days</p>
          </div>
        </div>
      </div>

      {/* Performance Trend Chart */}
      <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-slate-100">Performance Trends</h2>
          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="bg-slate-700 text-slate-200 border border-slate-600 rounded-lg px-3 py-2 text-sm"
          >
            <option value="onTimeShipping">On-Time Shipping</option>
            <option value="dockToStock">Dock to Stock</option>
            <option value="onTimeShippingPeak">On-Time Shipping (Peak)</option>
            <option value="shippingAccuracy">Shipping Accuracy</option>
            <option value="inventoryAccuracy">Inventory Accuracy</option>
            <option value="returnsProcessing">Returns Processing</option>
          </select>
        </div>
        <div className="h-64 flex items-end justify-between gap-1">
          {trendData.map((point, index) => {
            const maxValue = Math.max(...trendData.map(p => p.value));
            const minValue = Math.min(...trendData.map(p => p.value));
            const range = maxValue - minValue || 1;
            const normalizedHeight = ((point.value - minValue) / range) * 100;

            return (
              <div key={index} className="flex-1 flex flex-col items-center group">
                <div className="w-full flex items-end justify-center h-48 relative">
                  <div
                    className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t hover:from-blue-500 hover:to-blue-300 transition-all cursor-pointer relative group"
                    style={{
                      height: `${Math.max(normalizedHeight, 5)}%`,
                      minHeight: '4px'
                    }}
                  >
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-slate-700 text-slate-200 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {point.value.toFixed(1)}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-slate-400 mt-2 transform -rotate-45 origin-left whitespace-nowrap">
                  {point.date}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
        <h2 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-400" />
          Recent Activity
        </h2>
        <div className="space-y-3">
          {recentActivity.map((activity, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-700/50 transition-colors"
            >
              <div className={`p-2 rounded-lg border ${getActivityColor(activity.color)}`}>
                {getActivityIcon(activity.icon)}
              </div>
              <div className="flex-1">
                <p className="text-slate-200 text-sm">{activity.text}</p>
                <p className="text-slate-500 text-xs mt-0.5">{activity.time}</p>
              </div>
              <div>
                {activity.status === 'completed' && (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                )}
                {activity.status === 'processing' && (
                  <RefreshCw className="w-5 h-5 text-yellow-400 animate-spin" />
                )}
                {activity.status === 'in_progress' && (
                  <Clock className="w-5 h-5 text-blue-400" />
                )}
                {activity.status === 'warning' && (
                  <AlertCircle className="w-5 h-5 text-orange-400" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
